import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { AdminTraining } from "../pages/AdminTraining";
import { Page, Spinner, ErrorBox, EmptyBox, Card, Badge, AccountLink, Pager, REL, ACTION, fmt } from "./ui";

export function Crm() {
  const { data, error } = useFetch("/admin/crm");
  return (
    <Page title="Suivi AVELIS" subtitle="Comptes avec une relation commerciale, un intérêt exprimé ou une action prévue. Saisie manuelle uniquement." testid="admin-crm">
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : data.length === 0 ? <EmptyBox text="Aucun suivi en cours. Renseignez la relation depuis la fiche d'un compte (onglet Suivi AVELIS)." /> : (
        <div className="card divide-y divide-slate-100">{data.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm" data-testid="crm-row">
            <div className="min-w-0 flex-1"><AccountLink id={a.id} name={a.name} /><div className="text-xs text-slate-500">{a.owner_name} · {a.owner_email}</div></div>
            <Badge tone={a.relation === "client" ? "teal" : "slate"}>{REL[a.relation]}</Badge>
            {a.billing_interest && <Badge tone="amber">Intérêt facturation</Badge>}
            {a.next_action && <span className={`text-xs ${a.next_action_due && a.next_action_due <= new Date().toISOString().slice(0, 10) ? "font-bold text-red-700" : "text-slate-500"}`}>{a.next_action} · {fmt(a.next_action_due)}</span>}
          </div>
        ))}</div>
      )}
    </Page>
  );
}

export function Audit() {
  const [page, setPage] = useState(1);
  const { data, error } = useFetch(`/admin/audit?page=${page}`);
  return (
    <Page title="Journal d'activité" subtitle="Actions sensibles : auteur, date, compte concerné, nature de l'action." testid="admin-audit">
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : data.items.length === 0 ? <EmptyBox text="Aucune action enregistrée." /> : (
        <>
          <div className="card divide-y divide-slate-100">{data.items.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid="audit-row">
              <div><b>{ACTION[l.action] || l.action}</b> · {l.actor_name}{l.structure_name ? <> · <AccountLink id={l.structure_id} name={l.structure_name} /></> : ""}{l.target_name ? ` · ${l.target_name}` : ""}{l.detail ? <span className="text-slate-500"> · {l.detail}</span> : ""}</div>
              <span className="font-mono text-xs text-slate-400">{fmt(l.created_at, true)}</span>
            </div>
          ))}</div>
          <Pager page={page} total={data.total} per={50} setPage={setPage} />
        </>
      )}
    </Page>
  );
}

function SettingsForm({ s, reload }) {
  const [f, setF] = useState(s);
  useEffect(() => { setF(s); }, [s]);
  const num = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  const save = async () => { try { await api.put("/admin/settings", { ...f, reminder_days: String(f.reminder_days).split(/[ ,;]+/).filter(Boolean).map(Number) }); toast.success("Paramètres enregistrés"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const F = ({ label, children }) => <label className="block"><span className="label">{label}</span>{children}</label>;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Général">
        <div className="space-y-3">
          {F({ label: "Validité des invitations (jours)", children: <input type="number" className="field" value={f.invite_days} onChange={num("invite_days")} data-testid="settings-invite-days" /> })}
          {F({ label: "Inactivité signalée après (jours)", children: <input type="number" className="field" value={f.inactivity_days} onChange={num("inactivity_days")} data-testid="settings-inactivity-days" /> })}
          {F({ label: "Délai minimum entre deux relances de mission (min)", children: <input type="number" className="field" value={f.relaunch_cooldown_min} onChange={num("relaunch_cooldown_min")} /> })}
          {F({ label: "Relances max par mission", children: <input type="number" className="field" value={f.relaunch_max} onChange={num("relaunch_max")} /> })}
        </div>
      </Card>
      <Card title="Relances de démarrage par e-mail">
        <div className="space-y-3">
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" className="h-5 w-5 accent-teal-600" checked={f.reminders_enabled} onChange={(e) => setF({ ...f, reminders_enabled: e.target.checked })} data-testid="settings-reminders-enabled" />Activer les relances</label>
          {F({ label: "Jours après la création du compte (ex. 3, 7)", children: <input className="field" value={Array.isArray(f.reminder_days) ? f.reminder_days.join(", ") : f.reminder_days} onChange={(e) => setF({ ...f, reminder_days: e.target.value })} data-testid="settings-reminder-days" /> })}
          <p className="text-xs text-slate-500">Envoyées chaque matin à 9h au responsable d'un compte dont le démarrage est incomplet. Une relance n'est jamais envoyée deux fois et s'arrête dès que toutes les étapes sont faites. {f.reminders_enabled ? <Badge tone="green">Activées</Badge> : <Badge>Désactivées</Badge>}</p>
        </div>
      </Card>
      <button className="btn-primary lg:col-span-2" onClick={save} data-testid="settings-save-btn">Enregistrer les paramètres</button>
    </div>
  );
}

export function AdminSettings() {
  const { data, error, reload } = useFetch("/admin/settings");
  return (
    <Page title="Paramètres" testid="admin-settings">
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : <SettingsForm s={data} reload={reload} />}
      <h2 className="mb-3 mt-10 text-lg font-bold">Formations (Centre d'aide)</h2>
      <AdminTraining />
    </Page>
  );
}
